import { postRequest } from '../../../shared/utils';
import { parseNumeric } from '../../../shared/utils/numberUtils';

type ResolveRowDeleteNotificationsParams = {
  screenGridId: number;
  deletedRowIds: Array<number | string>;
};

function fetchPendingRowDeleteNotifications(screenGridId: number) {
  return postRequest('/api/records', {
    columns: ['id', 'type', 'payload', 'is_resolved'],
    screenGridId,
    schema: 'npi',
    table: 'notification',
    filterModel: {
      items: [
        {
          field: 'is_resolved',
          operator: 'is',
          value: false,
        },
        {
          field: 'type',
          operator: 'is',
          value: 'ROW_DELETE',
        },
      ],
      logicOperator: 'and',
    },
  });
}

function getPayloadRowId(payload: unknown): number | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  return parseNumeric((payload as { rowId?: unknown }).rowId);
}

/**
 * Resolves pending `ROW_DELETE` notifications that belong to rows
 * successfully deleted in the current commit.
 *
 * Intended for administrator direct-delete flow so obsolete delete-request
 * notifications are marked as resolved.
 */
export async function resolveRowDeleteNotificationsForDeletedRows({
  screenGridId,
  deletedRowIds,
}: ResolveRowDeleteNotificationsParams): Promise<void> {
  const targetRowIds = new Set(
    deletedRowIds
      .map((rowId) => parseNumeric(rowId))
      .filter((rowId): rowId is number => rowId != null),
  );

  if (!targetRowIds.size) {
    return;
  }

  const notificationRecords = await fetchPendingRowDeleteNotifications(screenGridId);

  if (notificationRecords?.errors) {
    console.error('[resolveRowDeleteNotificationsForDeletedRows] Failed to fetch ROW_DELETE notifications for cleanup', {
      errors: notificationRecords.errors,
      screenGridId,
      deletedRowIds,
    });
    return;
  }

  const notificationRows = notificationRecords?.data?.npi_notification ?? [];
  const notificationIdsToResolve = notificationRows
    .map((row: { id?: unknown; payload?: unknown; is_resolved?: unknown }) => {
      if (row?.is_resolved === true) {
        return null;
      }

      const notificationId = parseNumeric(row?.id);
      const payloadRowId = getPayloadRowId(row?.payload);

      if (notificationId == null || payloadRowId == null || !targetRowIds.has(payloadRowId)) {
        return null;
      }

      return notificationId;
    })
    .filter((notificationId: number | null): notificationId is number => notificationId != null);

  if (!notificationIdsToResolve.length) {
    return;
  }

  const resolveResults = await Promise.all(
    notificationIdsToResolve.map((notificationId) =>
      postRequest(`/api/notification/resolveNotification/${notificationId}`, {
        isResolved: true,
      }),
    ),
  );

  const failedResolutions = resolveResults.filter((result) => result?.errors?.length);
  if (failedResolutions.length) {
    console.error(
      '[resolveRowDeleteNotificationsForDeletedRows] Failed to resolve some ROW_DELETE notifications after admin delete',
      {
        notificationIdsToResolve,
        failedCount: failedResolutions.length,
      },
    );
  }
}